<template>
	<div class="tabs-container">
		<div class="tabs-header">
			<slot name="toolbar"></slot>
			<v-tabs
				v-model="activeTab"
				density="compact"
				show-arrows
				color="on-surface"
				bg-color="transparent"
				class="tabs-bar"
				:slider-size="0"
			>
				<v-tooltip
					v-for="(tab, index) in tabs"
					:key="index"
					:text="tab.title"
					location="bottom"
					open-delay="300"
					:disabled="!isLabelTruncated[index]"
				>
					<template #activator="{ props: tooltipProps }">
						<v-tab
							:value="index"
							class="tab-item"
							:style="activeTab === index ? tabActiveStyle(tab) : {}"
							v-bind="tooltipProps"
						>
							<span class="tab-curve tab-curve--left"></span>
							<span class="tab-curve tab-curve--right"></span>
							<v-icon v-if="tab.icon" :color="tab.color" size="small" class="tab-icon">{{
								tab.icon
							}}</v-icon>
							<span class="tab-label" :ref="(el: any) => setLabelRef(el, index)">{{ tab.title }}</span>
							<span v-if="index !== 0" class="tab-close-btn" @click.stop="closeTab(index)">
								<v-icon size="14">mdi-close</v-icon>
							</span>
						</v-tab>
					</template>
				</v-tooltip>
			</v-tabs>
		</div>

		<div class="tabs-content">
			<slot name="overlay" :active-tab="activeTab"></slot>
			<!-- Graph Tab (Always index 0) -->
			<div v-show="activeTab === 0" class="tab-content">
				<slot name="graph"></slot>
			</div>

			<!-- Dynamic Iframe Tabs -->
			<div
				v-for="(tab, index) in tabs"
				:key="index"
				v-show="activeTab === index && index !== 0"
				class="tab-content"
			>
				<template v-if="tab.type === 'iframe'">
					<div
						v-if="isImage(tab.url)"
						class="d-flex align-center justify-center tab-image"
						style="width: 100%; height: 100%"
					>
						<img
							:src="tab.url"
							style="
								max-width: 100%;
								max-height: 100%;
								object-fit: contain;
								pointer-events: none;
								user-select: none;
							"
						/>
					</div>
					<iframe
						v-else
						:src="tab.url"
						class="tab-iframe"
						frameborder="0"
						sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
						referrerpolicy="no-referrer"
					></iframe>
				</template>
				<template v-else-if="tab.type === 'markdown'">
					<div class="tab-markdown markdown-content" v-html="marked.parse(tab.description || '')"></div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onMounted, onUpdated, onBeforeUnmount } from "vue";
import { useGraphStore } from "../../../../store/graph";
import { marked } from "marked";

const graphStore = useGraphStore();
const activeTab = ref(0);

interface Tab {
	title: string;
	type: "graph" | "iframe" | "markdown";
	url?: string;
	description?: string;
	icon?: string;
	color?: string;
	notificationId?: string;
}

const tabs = ref<Tab[]>([{ title: "Graph", type: "graph" }]);

const notificationTabs = computed(() => graphStore.descriptor.notifications.filter((n: any) => n.display === "tab"));

watch(
	notificationTabs,
	(newNotifications) => {
		// Remove tabs for notifications that no longer exist
		const newIds = newNotifications.map((n: any) => n.id);
		const currentActiveTab = tabs.value[activeTab.value];

		for (let i = tabs.value.length - 1; i >= 0; i--) {
			const tab = tabs.value[i];
			if (tab.notificationId && !newIds.includes(tab.notificationId)) {
				tabs.value.splice(i, 1);
			}
		}

		// Add new tabs for new notifications
		let newlyAddedIndex = -1;
		newNotifications.forEach((n: any) => {
			const existingTab = tabs.value.find((t) => t.notificationId === n.id);
			const tabType = n.context?.tab_url ? "iframe" : "markdown";
			if (existingTab) {
				// Update existing
				existingTab.title = n.label || "Notification";
				existingTab.type = tabType;
				existingTab.url = n.context?.tab_url;
				existingTab.description = n.description || "";
				existingTab.icon = n.icon || "mdi-bell";
				existingTab.color = n.color;
			} else {
				// Add new
				tabs.value.push({
					title: n.label || "Notification",
					type: tabType,
					url: n.context?.tab_url,
					description: n.description || "",
					icon: n.icon || "mdi-bell",
					color: n.color,
					notificationId: n.id,
				});
				newlyAddedIndex = tabs.value.length - 1;
			}
		});

		if (newlyAddedIndex !== -1) {
			activeTab.value = newlyAddedIndex;
		} else if (currentActiveTab) {
			// Restore active tab index if possible
			const newIndex = tabs.value.indexOf(currentActiveTab);
			if (newIndex !== -1) {
				activeTab.value = newIndex;
			} else {
				// If active tab was removed, default to 0 (Graph) or clamp
				activeTab.value = Math.min(activeTab.value, tabs.value.length - 1);
			}
		} else {
			activeTab.value = 0;
		}
	},
	{ deep: true, immediate: true },
);

function closeTab(index: number) {
	const tab = tabs.value[index];
	if (tab.notificationId) {
		if (graphStore.descriptor?.notification?.remove) {
			graphStore.descriptor.notification.remove(tab.notificationId);
		}
	}
	tabs.value.splice(index, 1);
	if (activeTab.value >= tabs.value.length) {
		activeTab.value = tabs.value.length - 1;
	}
}

function tabActiveStyle(tab: Tab) {
	if (!tab.color) return {};
	return { color: tab.color };
}

const labelRefs: Record<number, HTMLElement | null> = {};
const isLabelTruncated = reactive<Record<number, boolean>>({});

function setLabelRef(el: HTMLElement | null, index: number) {
	labelRefs[index] = el;
}

function checkTruncation() {
	for (const [index, el] of Object.entries(labelRefs)) {
		if (el) {
			isLabelTruncated[Number(index)] = el.scrollWidth > el.clientWidth;
		}
	}
}

const resizeObserver = new ResizeObserver(() => checkTruncation());

onMounted(() => {
	checkTruncation();
	const container = document.querySelector(".tabs-header");
	if (container) resizeObserver.observe(container);
});

onUpdated(() => nextTick(checkTruncation));

onBeforeUnmount(() => resizeObserver.disconnect());

watch(
	() => tabs.value.map((t) => t.title),
	() => nextTick(checkTruncation),
);

function isImage(url?: string) {
	if (!url) return false;
	return /\.(jpeg|jpg|gif|png|svg|webp)($|\?)/i.test(url);
}
</script>

<style scoped>
.tabs-container {
	display: flex;
	flex-direction: column;
	height: 100%;
	width: 100%;
}

.tabs-header {
	flex: 0 0 auto;
	position: relative;
	z-index: 1;
	background-color: rgb(var(--v-theme-contained));
	padding: 6px 6px 0 6px;
	display: flex;
	align-items: flex-end;
}

.tabs-content {
	flex: 1 1 auto;
	position: relative;
	overflow: hidden;
}

.tab-content {
	width: 100%;
	height: 100%;
}

.tab-iframe {
	width: 100%;
	height: 100%;
	border: none;
}

.tab-image {
	padding: 10px;
}

.tab-markdown {
	padding: 24px 32px;
	overflow-y: auto;
	height: 100%;
	max-width: 900px;
}

.markdown-content :deep(p) {
	margin-bottom: 8px;
}
.markdown-content :deep(h1) {
	font-size: 1.6rem;
	font-weight: bold;
	margin: 16px 0 8px;
}
.markdown-content :deep(h2) {
	font-size: 1.35rem;
	font-weight: bold;
	margin: 14px 0 6px;
}
.markdown-content :deep(h3) {
	font-size: 1.15rem;
	font-weight: bold;
	margin: 12px 0 4px;
}
.markdown-content :deep(ul),
.markdown-content :deep(ol) {
	padding-left: 20px;
	margin-bottom: 8px;
}
.markdown-content :deep(table) {
	border-collapse: collapse;
	margin: 15px 0;
}
.markdown-content :deep(th),
.markdown-content :deep(td) {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
	padding: 6px 13px;
}
.markdown-content :deep(blockquote) {
	border-left: 4px solid rgba(var(--v-theme-on-surface), 0.2);
	padding-left: 15px;
	color: rgba(var(--v-theme-on-surface), 0.7);
	margin: 15px 0;
}
.markdown-content :deep(pre) {
	background-color: rgba(var(--v-theme-on-surface), 0.05);
	padding: 10px;
	border-radius: 4px;
	overflow: auto;
	margin: 15px 0;
	font-family: monospace;
}
.markdown-content :deep(pre code) {
	background-color: transparent;
	padding: 0;
}
.markdown-content :deep(code) {
	background-color: rgba(var(--v-theme-on-surface), 0.05);
	padding: 2px 4px;
	border-radius: 3px;
	font-family: monospace;
}

/* Brave-style tab bar */
.tabs-bar {
	flex: 1;
	overflow: visible;
}

.tabs-header :deep(.v-slide-group__prev),
.tabs-header :deep(.v-slide-group__next) {
	min-width: 28px;
	flex: 0 0 28px;
}

.tabs-header :deep(.v-slide-group__content) {
	gap: 2px;
	overflow: visible;
	padding-left: 8px;
}

.tabs-header :deep(.v-slide-group) {
	overflow: visible;
}

.tabs-header :deep(.v-slide-group__container) {
	overflow: visible;
}

.tabs-header :deep(.tab-item) {
	text-transform: none;
	letter-spacing: 0.01em;
	font-size: 1rem;
	border-radius: 10px 10px 0 0;
	min-width: 80px;
	max-width: 220px;
	height: 36px;
	padding: 0 12px;
	background-color: transparent;
	color: rgba(var(--v-theme-on-surface), 0.6);
	transition: background-color 0.1s ease, color 0.1s ease;
	overflow: hidden;
}

.tabs-header :deep(.tab-item .v-btn__content) {
	overflow: hidden;
	min-width: 0;
}

.tabs-header :deep(.tab-item .v-btn__overlay),
.tabs-header :deep(.tab-item .v-tab__overlay) {
	transition: opacity 0.1s ease;
}

.tabs-header :deep(.tab-item:hover) {
	background-color: rgba(var(--v-theme-on-surface), 0.06);
	color: rgba(var(--v-theme-on-surface), 0.8);
}

.tabs-header :deep(.tab-item.v-tab--selected) {
	background-color: rgb(var(--v-theme-surface));
	color: #4cb6ac;
	position: relative;
	z-index: 1;
	overflow: visible;
}

.tabs-header :deep(.tab-item.v-tab--selected .v-btn__content) {
	overflow: hidden;
	min-width: 0;
}

.tab-curve {
	position: absolute;
	bottom: 0;
	width: 8px;
	height: 8px;
	pointer-events: none;
	display: none;
}

.tab-curve--left {
	left: -8px;
	background: radial-gradient(circle at 0 0, transparent 8px, rgb(var(--v-theme-surface)) 8px);
}

.tab-curve--right {
	right: -8px;
	background: radial-gradient(circle at 100% 0, transparent 8px, rgb(var(--v-theme-surface)) 8px);
}

.tabs-header :deep(.tab-item.v-tab--selected .tab-curve) {
	display: block;
}

/* Hide the default Vuetify slider indicator */
.tabs-header :deep(.v-tab__slider) {
	display: none;
}

.tab-icon {
	margin-right: 6px;
	flex-shrink: 0;
}

.tab-label {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.tab-close-btn {
	opacity: 0.4;
	margin-left: 4px;
	flex-shrink: 0;
	cursor: pointer;
	border-radius: 50%;
	width: 22px;
	height: 22px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: opacity 0.15s ease, background-color 0.15s ease;
}

.tab-close-btn:hover {
	opacity: 1;
	background-color: rgba(var(--v-theme-on-surface), 0.12);
}
</style>
